import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { TreatmentPlanResponse, TreatmentPlanAppointment } from "../../../services/treatmentPlanService";
import { selectIsProvider } from "../../../store/selectors/authSelectors";
import { getCanEditTreatmentPlan } from "../../../utils/treatmentPlanDisplayUtils";
import { useTreatmentPlanAppointments } from "./useTreatmentPlanAppointments";
import { useTreatmentPlanPageData } from "./useTreatmentPlanPageData";

type NotificationSeverity = "success" | "error";

interface NotificationState {
  open: boolean;
  message: string;
  severity: NotificationSeverity;
}

export function useTreatmentPlanPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isProvider = useSelector(selectIsProvider);

  const [notification, setNotification] = useState<NotificationState>({
    open: false,
    message: "",
    severity: "success",
  });

  const showNotification = useCallback(
    (message: string, severity: NotificationSeverity = "success") => {
      setNotification({ open: true, message, severity });
    },
    [],
  );

  const hideNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const appointmentsRef = useRef<TreatmentPlanAppointment[]>([]);
  const syncAppointmentsFromPlanRef = useRef<
    (plan: TreatmentPlanResponse) => void
  >(() => undefined);

  const getAppointments = useCallback(() => appointmentsRef.current, []);

  const {
    treatmentPlan,
    setTreatmentPlan,
    loading,
    error,
    cdtCodes,
    fetchTreatmentPlan,
    handleSaveHeaderDetails,
    handleSaveTreatmentSummary,
    handleSaveProviderNote,
    handleSavePatientSummary,
  } = useTreatmentPlanPageData({
    planId: id,
    getAppointments,
    syncAppointmentsFromPlan: (plan) =>
      syncAppointmentsFromPlanRef.current(plan),
    showNotification,
    t,
  });

  const fetchAndSyncPlan = useCallback(
    async (force = false) => {
      const data = await fetchTreatmentPlan(force);
      if (data) {
        syncAppointmentsFromPlanRef.current(data);
      }
      return data;
    },
    [fetchTreatmentPlan],
  );

  const canEditTreatmentPlan = treatmentPlan
    ? getCanEditTreatmentPlan(isProvider, treatmentPlan.status)
    : false;

  const appointmentsState = useTreatmentPlanAppointments({
    treatmentPlan,
    setTreatmentPlan,
    planId: id,
    cdtCodes,
    canEditAppointments: canEditTreatmentPlan,
    showNotification,
    onRefetchPlan: fetchAndSyncPlan,
    t,
  });

  appointmentsRef.current = appointmentsState.appointments;
  syncAppointmentsFromPlanRef.current =
    appointmentsState.syncAppointmentsFromPlan;

  useEffect(() => {
    void fetchAndSyncPlan();
  }, [fetchAndSyncPlan]);

  return {
    id,
    t,
    isProvider,
    canEditTreatmentPlan,
    notification,
    showNotification,
    hideNotification,
    treatmentPlan,
    loading,
    error,
    fetchAndSyncPlan,
    handleSaveHeaderDetails,
    handleSaveTreatmentSummary,
    handleSaveProviderNote,
    handleSavePatientSummary,
    ...appointmentsState,
  };
}
